import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { LogoMark, Wordmark } from '../components/Logo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import ThemeToggle from '../components/ThemeToggle';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';

const LOCK_KEY = 'mtsr_login_attempts';
const MAX = 5;
const WINDOW = 15 * 60 * 1000;

function getAttempts() {
  try {
    const a = JSON.parse(localStorage.getItem(LOCK_KEY) || '[]');
    return a.filter((t) => Date.now() - t < WINDOW);
  } catch { return []; }
}

const DEMO = [
  { label: 'Bendahara', role: 'bendahara', email: 'bendaharamtsr@gmail.com' },
  { label: 'Guru', role: 'guru', email: 'guru.bukhari@mtsr.edu.my' },
  { label: 'Orangtua', role: 'orangtua', email: 'keluarga.rahman@gmail.com' },
];

export default function Login() {
  const { t } = useTranslation();
  const { session, profile, signIn } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (session && profile) {
      navigate(`/${profile.role}`, { replace: true });
    }
  }, [session, profile, navigate]);

  const handleLogin = async (targetEmail, targetPassword) => {
    const attempts = getAttempts();
    if (attempts.length >= MAX) {
      toast.error('Terlalu banyak percubaan. Sila cuba lagi dalam 15 minit.');
      return;
    }

    setLoading(true);
    try {
      const prof = await signIn(targetEmail, targetPassword);
      localStorage.removeItem(LOCK_KEY);
      toast.success('Berjaya log masuk.');
      navigate(`/${prof?.role || 'bendahara'}`, { replace: true });
    } catch (err) {
      setLoading(false);
      localStorage.setItem(LOCK_KEY, JSON.stringify([...attempts, Date.now()]));
      toast.error('Emel atau kata laluan tidak sah.');
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    await handleLogin(email.trim(), password);
  };

  const selectDemo = async (d) => {
    setEmail(d.email);
    setPassword('Mtsr@2026');
    // Log masuk pantas serta-merta tanpa perlu klik butang dua kali
    await handleLogin(d.email, 'Mtsr@2026');
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-card">
      {/* Brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 text-white overflow-hidden"
           style={{ background: 'linear-gradient(160deg, #1E3A8A 0%, #172554 100%)' }}>
        <div className="absolute inset-0 islamic-pattern opacity-[0.08]" />
        <div className="relative flex items-center gap-3">
          <LogoMark size={44} />
          <span className="text-3xl font-extrabold">
            <span className="text-white">MTSR</span><span style={{ color: '#34D399' }}>pay</span>
          </span>
        </div>
        <div className="relative">
          <h1 className="text-4xl font-extrabold leading-tight">Urus yuran sekolah<br />dengan tenang &amp; amanah.</h1>
          <p className="mt-4 text-lg text-blue-100 max-w-md">{t('tagline')}. Rekod bayaran kilat, resit automatik, laporan jelas — untuk sekolah, tahfiz &amp; tadika.</p>
        </div>
        <div className="relative text-sm text-blue-200/80">© 2026 MTSRpay · Selamat &amp; sulit</div>
      </div>

      {/* Form panel */}
      <div className="flex flex-col p-6 sm:p-12">
        <div className="flex justify-between items-center lg:hidden mb-8">
          <div className="flex items-center gap-2"><LogoMark size={36} /><Wordmark size="text-xl" /></div>
          <div className="flex items-center gap-2"><ThemeToggle /><LanguageSwitcher /></div>
        </div>
        <div className="hidden lg:flex justify-end gap-2"><ThemeToggle /><LanguageSwitcher /></div>

        <div className="flex-1 flex flex-col justify-center max-w-md w-full mx-auto">
          <h2 className="text-3xl font-bold text-foreground">{t('masuk')}</h2>
          <p className="mt-2 text-muted-foreground">Log masuk ke akaun anda untuk teruskan.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">{t('emel')}</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                     placeholder="nama@contoh.com" className="h-12 text-base" data-testid="login-email" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="password" className="shrink-0">{t('kataLaluan')}</Label>
                <Link to="/lupa-kata-laluan" className="text-sm font-medium text-brand hover:underline whitespace-nowrap text-right" data-testid="forgot-link">{t('lupaKataLaluan')}</Link>
              </div>
              <div className="relative">
                <Input id="password" type={show ? 'text' : 'password'} required value={password}
                       onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                       className="h-12 text-base pr-11" data-testid="login-password" />
                <button type="button" onClick={() => setShow((s) => !s)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-muted-foreground" tabIndex={-1}>
                  {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading}
                    className="w-full h-12 text-base font-semibold bg-brand text-white hover:bg-brand-royal active:scale-[0.98] transition-all"
                    data-testid="login-submit">
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  <span>Memproses...</span>
                </>
              ) : (
                <span>{t('logMasuk')}</span>
              )}
            </Button>
          </form>

          <div className="mt-6">
            <p className="text-xs text-muted-foreground mb-2">Akaun demo pantas (1-klik terus masuk):</p>
            <div className="flex flex-wrap gap-2">
              {DEMO.map((d) => (
                <button
                  key={d.email}
                  disabled={loading}
                  onClick={() => selectDemo(d)}
                  data-testid={`demo-${d.label.toLowerCase()}`}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl bg-muted text-foreground hover:bg-brand-light hover:text-brand border border-border transition-all active:scale-95 disabled:opacity-50"
                >
                  <span>{d.label}</span>
                  <ArrowRight className="h-3 w-3 opacity-60" />
                </button>
              ))}
            </div>
          </div>

          <p className="mt-8 text-sm text-muted-foreground text-center">
            {t('tiadaAkaun')}{' '}
            <Link to="/daftar" className="font-semibold text-brand hover:underline" data-testid="to-register">{t('daftarSekarang')}</Link>
          </p>
          <p className="mt-2 text-sm text-muted-foreground text-center">
            <Link to="/daftar-guru" className="font-semibold text-brand hover:underline" data-testid="to-register-guru">{t('daftarSebagaiGuru')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
